import {
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  ChevronRight,
  DoubleArrow,
  ExitToApp,
  Folder,
  Help,
  Home,
  People,
  PlaylistAdd,
  Settings,
} from '@material-ui/icons'
import { Discount, Newspaper, Support } from '@mui/icons-material'
import clsx from 'clsx'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { logout } from '../../actions/userActions'
import { SIDE_NAV_RESET } from '../../constants/navigationConstants'
import { Role } from '../../enum'
import { Context } from '../../providers/ProjectContextProvider'
import PromoCodes from '../Admin/PromoCodes'
import Reporting from '../Admin/Reporting'
import Dashboard from '../Dashboard/Dashboard'
import HelpCenter from '../HelpCenter/HelpCenter'
import MachineLibrary from '../Machine/MachineLibrary/MachineLibrary'
import PartLibrary from '../Part/PartLibrary/PartLibrary'
import ProgramLibrary from '../Program/ProgramLibrary/ProgramLibrary'
import ProjectDetail from '../Project/ProjectDetail/ProjectDetail'
import ProjectLibrary from '../Project/ProjectLibrary/ProjectLibrary'
import Preferences from '../User/Preferences/Preferences'
import UserLibrary from '../User/UserLibrary/UserLibrary'
import HelpCenterBar from './HelpCenterBar'

const drawerWidth = 250

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: `calc(100vw - 20px)`,
    position: 'unset',
    marginLeft: 24,
  },
  appBarShift: {
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 24,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
}))

interface Props {
  match: { params: { location: string } }
}

const MyQuality = ({ match }: Props) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const location = match.params.location

  const classes = useStyles()

  const { setShowContactSupport } = useContext(Context)

  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [drawerIsHovering, setDrawerIsHovering] = useState(false)

  const date = new Date()

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  const { close: sideNavClose, open: sideNavOpen } = useSelector(
    (state: RootStateOrAny) => state.sideNav
  )

  useEffect(() => {
    if (!userInfo) {
      history.push(`/login?redirect=${window.location.pathname}`)
    }

    if (sideNavClose) {
      setOpen(false)
      dispatch({ type: SIDE_NAV_RESET })
    }

    if (sideNavOpen) {
      setOpen(true)
      dispatch({ type: SIDE_NAV_RESET })
    }
  }, [userInfo, dispatch, history, sideNavClose, sideNavOpen])

  const toggleDrawer = () => {
    let newState = open ? false : true
    setOpen(newState)
  }

  const handleLibrariesClick = () => {
    if (!open) {
      setOpen(true)
      setTimeout(() => setExpanded(true), 200)
      setDrawerIsHovering(false)
    } else {
      setExpanded(!expanded)
    }
  }

  const logoutHandler = () => {
    dispatch(logout())
  }

  const handleLinkClick = (destination: string) => {
    setOpen(false)
    history.push(`/myquality/${destination}`)
  }

  return (
    <div
      className={`side-nav-container ${open ? 'open' : 'closed'} ${
        drawerIsHovering ? 'hover' : ''
      }`}
    >
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
        onMouseEnter={() => setDrawerIsHovering(open ? false : true)}
        onMouseLeave={() => setDrawerIsHovering(false)}
      >
        {userInfo && (
          <div className="flex space-between flex-column inner">
            <div>
              <div className="user-info hide-on-hover text-center">
                <div className="name fs14">
                  {userInfo.firstName} {userInfo.lastName}
                </div>
              </div>

              {userInfo.isAdmin ? (
                <List className="protect-style">
                  <ListItem
                    disableGutters={true}
                    button
                    component="a"
                    className={location === 'dashboard' ? 'active' : ''}
                    onClick={() => handleLinkClick('dashboard')}
                  >
                    <ListItemIcon>
                      <Home />
                    </ListItemIcon>
                    <ListItemText primary={'Project list'} />
                  </ListItem>

                  <ListItem
                    disableGutters={true}
                    button
                    className={location === 'users' ? 'active' : ''}
                    onClick={() => handleLinkClick('users')}
                  >
                    <ListItemIcon>
                      <People />
                    </ListItemIcon>
                    <ListItemText primary={'User admin'} />
                  </ListItem>

                  {userInfo.role === Role.MANAGER && (
                    <ListItem
                      disableGutters={true}
                      button
                      className={location === 'reporting' ? 'active' : ''}
                      onClick={() => handleLinkClick('reporting')}
                    >
                      <ListItemIcon>
                        <Newspaper />
                      </ListItemIcon>
                      <ListItemText primary={'Reporting'} />
                    </ListItem>
                  )}

                  {userInfo.role === Role.MANAGER && (
                    <ListItem
                      disableGutters={true}
                      button
                      className={location === 'promo-codes' ? 'active' : ''}
                      onClick={() => handleLinkClick('promo-codes')}
                    >
                      <ListItemIcon>
                        <Discount />
                      </ListItemIcon>
                      <ListItemText primary={'Promo Codes'} />
                    </ListItem>
                  )}
                </List>
              ) : (
                <>
                  <List className="protect-style">
                    <ListItem
                      disableGutters={true}
                      button
                      component="a"
                      className={location === 'dashboard' ? 'active' : ''}
                      onClick={() => handleLinkClick('dashboard')}
                    >
                      <ListItemIcon>
                        <Home />
                      </ListItemIcon>
                      <ListItemText primary={'Dashboard'} />
                    </ListItem>

                    <ListItem
                      disableGutters={true}
                      button
                      className={location === 'projects' ? 'active' : ''}
                      onClick={() => handleLinkClick('projects')}
                    >
                      <ListItemIcon>
                        <Folder />
                      </ListItemIcon>
                      <ListItemText primary={'Projects'} />
                    </ListItem>

                    <ListItem
                      disableGutters={true}
                      button
                      onClick={handleLibrariesClick}
                    >
                      <ListItemIcon>
                        <PlaylistAdd />
                      </ListItemIcon>
                      <ListItemText primary={'Libraries'} />
                      <ListItemIcon>
                        <ChevronRight
                          className={`hide-on-closed ${
                            expanded ? 'rotate' : ''
                          }`}
                        />
                      </ListItemIcon>
                    </ListItem>
                  </List>
                  <Collapse in={expanded && open} timeout="auto" unmountOnExit>
                    <List className="protect-style dotted">
                      <ListItem
                        disableGutters={true}
                        button
                        component="a"
                        className={location === 'machines' ? 'active' : ''}
                        onClick={() => handleLinkClick('machines')}
                      >
                        <ListItemText primary={'Machines'} />
                      </ListItem>
                      <ListItem
                        disableGutters={true}
                        button
                        component="a"
                        className={location === 'parts' ? 'active' : ''}
                        onClick={() => handleLinkClick('parts')}
                      >
                        <ListItemText primary={'Parts'} />
                      </ListItem>
                      <ListItem
                        disableGutters={true}
                        button
                        component="a"
                        onClick={() => handleLinkClick('programs')}
                        className={location === 'programs' ? 'active' : ''}
                      >
                        <ListItemText primary={'Programs'} />
                      </ListItem>
                    </List>
                  </Collapse>
                </>
              )}
            </div>
            <div>
              <List className="bottom-links m-b-32 p-t-16 p-b-16">
                <ListItem
                  disableGutters={true}
                  button
                  component="a"
                  onClick={() => handleLinkClick('preferences')}
                >
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText primary={'Preferences'} />
                </ListItem>

                <ListItem
                  disableGutters={true}
                  button
                  component="a"
                  onClick={() => handleLinkClick('help-center')}
                >
                  <ListItemIcon>
                    <Help />
                  </ListItemIcon>
                  <ListItemText primary={'Help center'} />
                </ListItem>

                <ListItem
                  disableGutters={true}
                  button
                  component="a"
                  onClick={() => setShowContactSupport(true)}
                >
                  <ListItemIcon>
                    <Support />
                  </ListItemIcon>
                  <ListItemText primary={'Support'} />
                </ListItem>

                <ListItem disableGutters={true} button onClick={logoutHandler}>
                  <ListItemIcon>
                    <ExitToApp />
                  </ListItemIcon>
                  <ListItemText primary={'Sign Out'} />
                </ListItem>
              </List>

              <div className="fs10 hide-on-closed white-text flex flex-column align-center">
                <div className="m-b-8">© Hexagon {date.getFullYear()}</div>
                <div
                  className="flex space-between"
                  style={{ color: 'white', width: '100%' }}
                >
                  <a
                    href="https://www.hexagonmi.com/en-US/about-us/privacy-policy"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="link-white"
                  >
                    Privacy policy
                  </a>
                  |
                  <a
                    href="https://www.hexagonmi.com/en-US/about-us/terms-of-use"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="link-white"
                  >
                    Terms of use
                  </a>
                  |
                  <a
                    href="https://www.hexagonmi.com/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="link-white"
                  >
                    Hexagon MI
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {userInfo && (
        <>
          <div
            key={location}
            id="app-bar-offset"
            className={clsx(classes.appBar, {
              [classes.appBarShift]: open,
            })}
          >
            {userInfo.showHelpCenter && location !== 'help-center' && (
              <HelpCenterBar />
            )}
            {userInfo.isAdmin ? (
              <>
                {location === 'dashboard' && <ProjectLibrary />}
                {location === 'projectnotfound' && (
                  <ProjectLibrary projectNotFound={true} />
                )}
                {location === 'users' && <UserLibrary />}

                {location === 'reporting' && userInfo.role === Role.MANAGER && (
                  <Reporting />
                )}

                {location === 'promo-codes' &&
                  userInfo.role === Role.MANAGER && <PromoCodes />}
              </>
            ) : (
              <>
                {location === 'dashboard' && <Dashboard />}
                {location === 'projectnotfound' && (
                  <Dashboard projectNotFound={true} />
                )}

                {location === 'projects' && <ProjectLibrary />}
                {location === 'parts' && <PartLibrary />}
                {location === 'machines' && <MachineLibrary />}
                {location === 'programs' && <ProgramLibrary />}
              </>
            )}

            {location === 'project' && <ProjectDetail />}
            {location === 'preferences' && <Preferences />}
            {location === 'help-center' && <HelpCenter />}
          </div>
          <IconButton
            className={`side-nav-toggle ${drawerIsHovering ? 'hover' : ''}`}
            color="secondary"
            onClick={() => toggleDrawer()}
          >
            <DoubleArrow />
          </IconButton>
        </>
      )}
    </div>
  )
}

export default MyQuality
